import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import ENV from "../env"

interface DailyVolumeResponse {
  date: string
  quote_token_process: string
  daily_volume_usd: number
  daily_volume_quote_token: number
}

function useDailyVolume() {
  // Memoize timestamps so they don't change on every render
  // Update only once per hour to avoid unnecessary refetches
  const timestamps = useMemo(() => {
    const now = Date.now()
    // Round to the nearest hour to stabilize the query key
    const roundedNow = Math.floor(now / (1000 * 60 * 60)) * (1000 * 60 * 60)
    const startTimestamp = Math.floor(roundedNow / 1000) - 30 * 24 * 60 * 60
    const endTimestamp = Math.floor(roundedNow / 1000)

    return { startTimestamp, endTimestamp }
  }, [])

  return useQuery({
    queryKey: ["dailyVolume", timestamps.startTimestamp, timestamps.endTimestamp],
    queryFn: async () => {
      const supabaseUrl = (ENV.VITE_SUPABASE_URL ?? "").replace(/\/$/, "")
      const apiKey = ENV.VITE_SUPABASE_ANON_KEY

      if (!supabaseUrl || !apiKey) {
        throw new Error("Supabase environment variables are not set")
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/volume`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          "Start-Timestamp": timestamps.startTimestamp,
          "End-Timestamp": timestamps.endTimestamp,
        }),
      })

      if (!response.ok) {
        throw new Error(`Supabase volume error! status: ${response.status}`)
      }

      const data = (await response.json()) as DailyVolumeResponse[] | null

      if (!data) {
        throw new Error("No data found")
      }

      return data
    },
    // Cache the data for 5 minutes to reduce unnecessary requests
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  })
}

export default useDailyVolume
